/**
 * Benefit keys that belong on the proposal (PropelX/Quotes) side only, not on
 * Build. Definitions stay shared in `plan-grid-core`; Build filters them out
 * of its rows instead.
 *
 * Currently empty. Rate guarantee, rate cap, and participation requirements
 * were filtered here until analysts needed to record the incumbent's terms:
 * the Quotes side-by-side renders its Incumbent column read-only from the RFP
 * plans, so Build is the only place those values can be entered.
 */
const PROPOSAL_ONLY_BENEFIT_KEYS: ReadonlySet<string> = new Set<string>();

/**
 * Voluntary Life's fixed child benefit-description rows (and their nested
 * `...Amount` siblings, each its own row after `buildRowData`), plus
 * `childLimitingAge` - the rows the flexible `childBenefitAmountsByAge` field
 * replaces when `btx-ch-benefits-field` is on (CD-1976 parity with Present).
 */
const CHILD_FIXED_AGE_BAND_KEYS: ReadonlySet<string> = new Set([
  "benefitDescriptionChildDependentAgesBirthTo14DaysType",
  "benefitDescriptionChildDependentAgesBirthTo14DaysAmount",
  "benefitDescriptionChildDependentAges15DaysTo6MonthsType",
  "benefitDescriptionChildDependentAges15DaysTo6MonthsAmount",
  "benefitDescriptionChildDependentAges6MonthsToLimitingAgeType",
  "benefitDescriptionChildDependentAges6MonthsToLimitingAgeAmount",
  "childLimitingAge",
]);

const CHILD_AGE_BANDS_KEY = "childBenefitAmountsByAge";

export interface HideProposalOnlyRowsOptions {
  /**
   * When true, hide Voluntary Life's three fixed child benefit-description
   * rows in favor of the flexible `childBenefitAmountsByAge` row. When false
   * (default), hide `childBenefitAmountsByAge` and keep the three fixed rows -
   * today's behavior.
   */
  childAgeBandsFieldEnabled?: boolean;
}

const isHiddenRow = (
  row: unknown,
  childAgeBandsFieldEnabled: boolean,
): boolean => {
  if (typeof row !== "object" || row === null || !("benefitKey" in row)) {
    return false;
  }

  const { benefitKey } = row;
  if (typeof benefitKey !== "string") {
    return false;
  }

  if (PROPOSAL_ONLY_BENEFIT_KEYS.has(benefitKey)) {
    return true;
  }

  return childAgeBandsFieldEnabled
    ? CHILD_FIXED_AGE_BAND_KEYS.has(benefitKey)
    : benefitKey === CHILD_AGE_BANDS_KEY;
};

/**
 * Drops proposal-only benefit rows from a built row set.
 *
 * Filters whole rows rather than the underlying benefit definitions: a nested
 * child is re-parented to the previous non-nested definition, so removing a
 * parent definition would re-home its children instead of removing them.
 */
export const hideProposalOnlyRows = <TRow>(
  rows: TRow[],
  options: HideProposalOnlyRowsOptions = {},
): TRow[] =>
  rows.filter(
    (row) => !isHiddenRow(row, options.childAgeBandsFieldEnabled ?? false),
  );
